import { useState } from "react";
import { useParams, Navigate, Link } from "react-router";
import {
  useReportFile,
  useRevokeSharedReport,
  useShareReport,
  useUserDetails,
} from "@/hooks";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { format } from "date-fns";
import { TZDate } from "@date-fns/tz";
import { es } from "date-fns/locale";
import { AgentBadge } from "@/components/agent-badge";
import { Download, Send, Loader2, ChevronLeft } from "lucide-react";
import { useReportDetails } from "@/hooks/use-report-details";
import { Separator } from "@/components/ui/separator";

export default function ReportDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState("");

  const { data: user } = useUserDetails();
  const reportFile = useReportFile();
  const {
    data: report,
    isLoading: isReportLoading,
    isError: isReportError,
  } = useReportDetails(id || "");
  const { mutateAsync: revokeSharedReportMutation, isPending: isUnsharing } =
    useRevokeSharedReport();
  const { mutateAsync: shareReportMutation, isPending: isSharingLoading } =
    useShareReport();

  if (!id) {
    return <Navigate to="/reportes" />;
  }

  if (user?.type === "anonymous") {
    return <Navigate to="/inicia-sesion?redirect=reportes" />;
  }

  return (
    <div className="w-full gap-3 grid">
      <div className="flex items-center gap-0">
        <Link
          to="/reportes"
          className="flex items-center gap-2 text-sm text-muted-foreground"
        >
          <ChevronLeft className="size-4" />
          Regresar
        </Link>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold">Detalles del Reporte</h1>
          <p className="text-xs text-muted-foreground">
            Información detallada del reporte seleccionado
          </p>
        </div>
      </div>
      <Separator />

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="col-span-3 lg:col-span-1">
          <CardContent>
            {isReportLoading ? (
              <div className="flex justify-center p-4">
                <Loader2 className="size-8 animate-spin text-muted-foreground" />
              </div>
            ) : !report ? (
              <div className="text-center p-4 text-muted-foreground">
                Reporte no encontrado
              </div>
            ) : (
              <div className="grid gap-4">
                <div>
                  <h3 className="text-sm font-medium">Identificadores</h3>
                  <div className="flex gap-2 items-center">
                    <p className="font-mono text-xs">
                      {report.projectId || report.optionalId}
                    </p> 
                    <span>-</span>
                    <p className="font-mono text-xs text-muted-foreground">{report.id}</p>
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-medium">Agente</h3>
                  <AgentBadge
                    variant={
                      report.agent as "lighting" | "noise_dose" | "temperature"
                    }
                  />
                </div>
                <div>
                  <h3 className="text-sm font-medium">Fecha de Creación</h3>
                  <div >
                  <p className="text-xs">
                    {format(new TZDate(report.generatedAt + "Z"), "PPP", {
                      locale: es,
                    })}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {format(
                      new TZDate(report.generatedAt + "Z"),
                      "hh:mm aaa z",
                      { locale: es }
                    )}
                  </p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
          <CardFooter className="grid gap-2  w-full">
            <Button
              onClick={() => setShareDialogOpen(true)}
              className="w-full text-xs gap-2 justify-start max-w-64"
              disabled={isReportLoading || !report}
            >
              <Send className="size-4" />
              Compartir Reporte
            </Button>
            <Button
              onClick={async () => {
                const url = await reportFile.mutateAsync({
                  reportId: report?.id ?? "",
                  filename: report?.outputPathPdf ?? "",
                });
                window.open(url, "_blank");
              }}
              variant="outline"
              className="w-full text-xs gap-2 justify-start max-w-64 hover:bg-accent/15 focus:bg-accent/15"
              disabled={isReportLoading || !report || !report.outputPathPdf}
            >
              <Download className="size-4" />
              Reporte PDF
            </Button>
            <Button
              onClick={async () => {
                const url = await reportFile.mutateAsync({
                  reportId: report?.id ?? "",
                  filename: report?.inputPath ?? "",
                });
                window.open(url, "_blank");
              }}
              variant="outline"
              className="w-full text-xs gap-2 justify-start max-w-64 hover:bg-accent/15 focus:bg-accent/15"
              disabled={isReportLoading || !report || !report.inputPath}
            >
              <Download className="size-4" />
              Datos Excel
            </Button>
            <Button
              onClick={async () => {
                const url = await reportFile.mutateAsync({
                  reportId: report?.id ?? "",
                  filename: report?.optionsPathJson ?? "",
                });
                window.open(url, "_blank");
              }}
              variant="outline"
              className="w-full text-xs gap-2 justify-start max-w-64 hover:bg-accent/15 focus:bg-accent/15"
              disabled={isReportLoading || !report || !report.optionsPathJson}
            >
              <Download className="size-4" />
              Opciones JSON
            </Button>
          </CardFooter>
        </Card>
        <Card className="col-span-3 lg:col-span-2">
          <CardHeader>
            <CardTitle>Usuarios invitados</CardTitle>
            <CardDescription>
              Los usuarios invitados pueden ver el reporte en el portal de clientes (clientes.bdino.app)
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isReportLoading ? (
              <div className="flex justify-center p-8">
                <Loader2 className="size-8 animate-spin text-muted-foreground" />
              </div>
            ) : isReportError ? (
              <div className="text-center p-4 text-destructive">
                Error al cargar los usuarios compartidos
              </div>
            ) : !report?.sharedWith || report?.sharedWith.length === 0 ? (
              <div className="text-center p-8 text-muted-foreground">
                Este reporte no ha sido compartido con ningún usuario.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Fecha de Liberación</TableHead>
                    <TableHead className="w-[100px]">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {report?.sharedWith.map((share) => (
                    <TableRow key={share.id}>
                      <TableCell className="font-medium">
                        {share.email}
                      </TableCell>
                      <TableCell>
                        {format(new TZDate(share.createdAt + "Z"), "PPP", {
                          locale: es,
                        })}
                        <span className="block text-xs text-muted-foreground">
                          {format(
                            new TZDate(share.createdAt + "Z"),
                            "hh:mm aaa z",
                            { locale: es }
                          )}
                        </span>
                      </TableCell>
                      <TableCell>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className='border-destructive text-destructive hover:border-destructive focus:border-destructive hover:bg-transparent focus:bg-transparent hover:text-destructive focus:text-destructive'
                            >
                              {isUnsharing ? (
                                <Loader2 className="size-4 animate-spin" />
                              ) : (
                                "Revocar"
                              )}
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>
                                ¿Revocar acceso?
                              </AlertDialogTitle>
                              <AlertDialogDescription>
                                Esta acción eliminará el acceso de{" "}
                                <u>{share.email}</u> a este reporte.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="hover:bg-accent/15 focus:bg-accent/15">Cancelar</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={async () =>
                                  await revokeSharedReportMutation({
                                    reportId: report?.id ?? "",
                                    sharedReportId: share.id,
                                  })
                                }
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                Revocar
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Compartir Reporte</DialogTitle>
            <DialogDescription>
              Comparte este reporte con otro usuario por correo
              electrónico.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="email">Correo Electrónico</Label>
              <Input
                id="email"
                placeholder="ejemplo@correo.com"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShareDialogOpen(false)}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="gap-2"
              onClick={async () =>
                await shareReportMutation({
                  reportId: report?.id ?? "",
                  email: recipientEmail,
                })
              }
              disabled={!recipientEmail.trim() || isSharingLoading}
            >
              {isSharingLoading ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  Compartiendo...
                </>
              ) : (
                <>
                  <Send className="size-4" />
                  Compartir
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
